<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Personas</span>
    </template>

    <template #actions>
      <router-link :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
      <template v-if="!isFlock">
        <button class="btn secondary" type="button" @click="reload" :disabled="saving || loading">
          Discard
        </button>
        <button
          class="btn primary"
          type="button"
          @click="save"
          :disabled="saving || loading || !dirty"
        >
          {{ saving ? 'Saving…' : 'Save changes' }}
        </button>
      </template>
    </template>

    <div v-if="loading" class="pageLoading">Loading…</div>
    <div v-else-if="loadError" class="errorBanner">{{ loadError }}</div>

    <template v-else>
      <div v-if="saveError" class="errorBanner">{{ saveError }}</div>

      <header class="pageHead">
        <h1 class="title">Personas for {{ playName }}</h1>
        <p class="sub">
          The buyer profiles this play targets. Each persona's titles guide which contacts
          get pulled in from prospecting and how the agent qualifies a contact for outreach.
        </p>
      </header>

      <div v-if="!draft.length" class="emptyState">
        <p>No personas yet.<template v-if="!isFlock"> Add one to start targeting buyers.</template></p>
        <button v-if="!isFlock" class="btn primary" type="button" @click="addPersona">+ Add persona</button>
      </div>

      <ol v-else class="personaList">
        <li v-for="(p, i) in draft" :key="p.localKey" class="personaCard">
          <div class="cardHead">
            <span class="personaInitials">{{ initials(p.name) }}</span>
            <input
              class="input nameInput"
              :readonly="isFlock"
              v-model="p.name"
              placeholder="e.g. VP of Sales"
              maxlength="200"
            />
            <button
              v-if="!isFlock"
              class="iconBtn"
              type="button"
              :title="`Remove ${p.name || 'persona'}`"
              @click="removePersona(i)"
            >
              <AppIcon name="close" :size="16" />
            </button>
          </div>

          <div class="formField">
            <label class="formLabel">Description</label>
            <textarea
              class="formTextarea"
              :readonly="isFlock"
              v-model="p.description"
              placeholder="What this persona cares about, how we help, etc."
              rows="3"
              maxlength="2000"
            />
          </div>

          <div class="formField">
            <label class="formLabel">Job titles to target</label>
            <input
              class="input"
              :readonly="isFlock"
              :value="p.explicitTitlesText"
              placeholder="VP of Sales, Head of Sales, Sales Director"
              @input="onTitlesInput($event, p, 'explicitTitlesText')"
            />
            <span class="hint">Comma-separated. The agent matches a contact when their title contains any of these.</span>
          </div>

          <div class="formField">
            <label class="formLabel">Titles to exclude</label>
            <input
              class="input"
              :readonly="isFlock"
              :value="p.excludedTitlesText"
              placeholder="Sales Enablement, RevOps"
              @input="onTitlesInput($event, p, 'excludedTitlesText')"
            />
            <span class="hint">Comma-separated. Contacts with these titles are skipped even if they match above.</span>
          </div>
        </li>
      </ol>

      <button v-if="draft.length && !isFlock" class="btn addRow" type="button" @click="addPersona">
        + Add persona
      </button>

      <section v-if="referenced.length" class="referencedSection">
        <h2 class="sectionTitle">Inherited from workspace</h2>
        <p class="sub">
          These personas are defined at the workspace level and referenced by this play.
          Edit them from the workspace settings.
        </p>
        <ul class="referencedList">
          <li v-for="r in referenced" :key="r.id" class="referencedRow">
            <span class="personaInitials">{{ initials(r.name) }}</span>
            <div class="referencedText">
              <span class="personaName">{{ r.name }}</span>
              <span class="personaMeta">{{ (r.explicit_titles || []).join(', ') || '—' }}</span>
            </div>
          </li>
        </ul>
      </section>
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { usePlayEngine, type PlayEngine } from '../composables/usePlayEngine'

interface PersonaApi {
  id: string
  name: string
  description?: string
  explicit_titles?: string[]
  titles_to_exclude?: string[]
}

interface DraftPersona {
  // Stable per-row key so v-for doesn't reuse DOM after delete/reorder.
  // We don't reuse server ids here because deleting a row would leave a
  // stale id mapping if the user later re-adds with the same name.
  localKey: number
  name: string
  description: string
  explicitTitlesText: string
  excludedTitlesText: string
}

const route = useRoute()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))

const playName = ref('')
const play = ref<{ engine?: PlayEngine } | null>(null)
const { isFlock } = usePlayEngine(play)
const draft = ref<DraftPersona[]>([])
const referenced = ref<PersonaApi[]>([])
const baseline = ref('')
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')

let nextLocalKey = 1
function toDraft(p: PersonaApi): DraftPersona {
  return {
    localKey: nextLocalKey++,
    name: p.name || '',
    description: p.description || '',
    explicitTitlesText: (p.explicit_titles || []).join(', '),
    excludedTitlesText: (p.titles_to_exclude || []).join(', '),
  }
}

function snapshot(rows: DraftPersona[]): string {
  return JSON.stringify(rows.map((p) => ({
    n: p.name.trim(),
    d: p.description.trim(),
    t: splitTitles(p.explicitTitlesText),
    x: splitTitles(p.excludedTitlesText),
  })))
}

const dirty = computed(() => snapshot(draft.value) !== baseline.value)

function initials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '·'
  return parts.slice(0, 2).map((s) => s[0].toUpperCase()).join('')
}

function splitTitles(text: string): string[] {
  return String(text || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 50)
}

function onTitlesInput(e: Event, p: DraftPersona, field: 'explicitTitlesText' | 'excludedTitlesText') {
  p[field] = (e.target as HTMLInputElement).value
}

function addPersona() {
  draft.value.push({
    localKey: nextLocalKey++,
    name: '',
    description: '',
    explicitTitlesText: '',
    excludedTitlesText: '',
  })
}

function removePersona(idx: number) {
  draft.value.splice(idx, 1)
}

async function reload() {
  loading.value = true
  loadError.value = ''
  saveError.value = ''
  try {
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
    const detail = resp?.play || null
    play.value = detail
    playName.value = detail?.name || ''
    const local = (detail?.personas?.local || []) as PersonaApi[]
    referenced.value = (detail?.personas?.referenced || []) as PersonaApi[]
    draft.value = local.map(toDraft)
    baseline.value = snapshot(draft.value)
  } catch (e: any) {
    loadError.value = e?.message || 'Failed to load personas'
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  saveError.value = ''
  try {
    const personas = draft.value
      .map((p) => ({
        name: p.name.trim(),
        description: p.description.trim(),
        explicit_titles: splitTitles(p.explicitTitlesText),
        titles_to_exclude: splitTitles(p.excludedTitlesText),
      }))
      .filter((p) => p.name)
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}/personas`, {
      method: 'PUT',
      body: { personas },
    })
    const local = (resp?.personas?.local || []) as PersonaApi[]
    referenced.value = (resp?.personas?.referenced || []) as PersonaApi[]
    draft.value = local.map(toDraft)
    baseline.value = snapshot(draft.value)
  } catch (e: any) {
    saveError.value = e?.message || 'Save failed'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value, page_type: 'sales_play_personas' },
  })
  await reload()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

.pageLoading { color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.errorBanner {
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-4);
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}

.pageHead {
  max-width: 720px;
  margin-bottom: var(--space-5);
}
.title {
  margin: 0 0 var(--space-2);
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.sub {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}

.emptyState {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  max-width: 720px;
  padding: var(--space-5);
  background: var(--color-bg-surface);
  border: 1px dashed var(--color-border-subtle);
  border-radius: var(--radius-lg);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}

.personaList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 720px;
}

.personaCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-neu-out-cream-sm);
}

.cardHead {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.personaInitials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.nameInput {
  flex: 1;
  font-weight: var(--fw-semibold);
  font-size: var(--fs-md);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-tertiary);
  transition:
    background var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}
.iconBtn:hover { background: var(--color-bg-subtle); color: var(--color-danger); }

.formField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.formLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}

.formTextarea {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-app);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  resize: vertical;
}
.formTextarea:focus { outline: none; border-color: var(--color-accent); }

.hint {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.addRow {
  align-self: flex-start;
  margin-top: var(--space-4);
}

.referencedSection {
  margin-top: var(--space-7);
  max-width: 720px;
}
.sectionTitle {
  margin: 0 0 var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.referencedList {
  list-style: none;
  margin: var(--space-3) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.referencedRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
}
.referencedText { display: flex; flex-direction: column; min-width: 0; }
.personaName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.personaMeta {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
</style>
